import type { Bones } from './clip.ts';
import { lerp, smooth, type Ease } from './curves.ts';

/** A bone's offset in a key pose: rotation x, y, z (radians), then optional translation x, y, z and scale. */
export type Ch = readonly [number, number, number, number?, number?, number?, number?];
export type KeyPose<N extends string> = Partial<Record<N, Ch>>;
/** A beat of a timeline: at `time` the figure reaches `pose`, arriving with `ease` (default smooth). */
export type Beat<N extends string> = readonly [number, KeyPose<N>, Ease?];

const ZERO: Ch = [0, 0, 0, 0, 0, 0, 1];

const chan = (c: Ch | undefined, i: number): number => (c ? (c[i] ?? (i === 6 ? 1 : 0)) : i === 6 ? 1 : 0);

/**
 * Plays key poses over time. Every bone named in any beat goes from beat to beat (a bone a beat leaves out is
 * at rest there). `lag` delays bones (seconds) so the extremities trail the body: the follow-through and
 * overlap that keep a swing from looking stiff.
 */
export const timeline = <N extends string>(b: Bones<N>, t: number, beats: readonly Beat<N>[], lag?: Partial<Record<N, number>>): void => {
  if (beats.length === 0) return;
  const names = new Set<N>();
  for (const [, p] of beats) for (const k of Object.keys(p) as N[]) names.add(k);
  for (const n of names) {
    const tb = t - (lag?.[n] ?? 0);
    let a = beats[0] as Beat<N>;
    let z = a;
    let k = 0;
    if (tb <= a[0]) z = a;
    else {
      let found = false;
      for (let i = 1; i < beats.length; i++) {
        const nb = beats[i] as Beat<N>;
        if (tb <= nb[0]) {
          a = beats[i - 1] as Beat<N>;
          z = nb;
          const sp = z[0] - a[0];
          k = (z[2] ?? smooth)(sp > 0 ? (tb - a[0]) / sp : 1);
          found = true;
          break;
        }
      }
      if (!found) a = z = beats[beats.length - 1] as Beat<N>;
    }
    const ca = a[1][n] ?? (a === z ? undefined : ZERO);
    const cz = z[1][n] ?? ZERO;
    const v = (i: number): number => (a === z ? chan(ca, i) : lerp(chan(ca, i), chan(cz, i), k));
    const bp = b[n];
    bp.rot(v(0), v(1), v(2)).pos(v(3), v(4), v(5)).scale(v(6));
  }
};

/** Adds a key pose (scaled by `w`) to the bones: for layering a held stance under a procedural motion. */
export const apply = <N extends string>(b: Bones<N>, p: KeyPose<N>, w = 1): void => {
  for (const n of Object.keys(p) as N[]) {
    const c = p[n] as Ch;
    b[n].rot(c[0] * w, c[1] * w, c[2] * w).pos((c[3] ?? 0) * w, (c[4] ?? 0) * w, (c[5] ?? 0) * w).scale(1 + ((c[6] ?? 1) - 1) * w);
  }
};
